
import { MaintenanceItem, MaintenanceItemDatePeriodUnit, MaintenanceItemMutation, Trip } from '@clients/myTrips';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import MaintenanceField from '@components/settings/maintenance-items/MaintenanceField.vue';
import MaintenanceInputwrap from '@components/settings/maintenance-items/MaintenanceInputwrap.vue';
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import DateInput from '@components/new-styleguide/form/DateInput.vue';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import NumberInput from '@components/new-styleguide/form/NumberInput.vue';
import { RouterFrom } from '@router/Routes';
import deepCopy from '@util/copy';
import goBackWithFallback from '@util/goBackWithFallback';
import { LoadState } from '@util/types';
import fnsAdd from 'date-fns/add';
import fnsFormatISO from 'date-fns/formatISO';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Location } from 'vue-router';
import { namespace } from 'vuex-class';

const SettingsModule = namespace('Settings');
const TriplogModule = namespace('Triplog');
const RouterModule = namespace('Router');

export type DueType = 'once' | 'repeat';

@Component({
  name: 'MaintenanceEdit',
  components: {
    DateInput,
    MaintenanceInputwrap,
    MaintenanceField,
    NumberInput,
    SettingsFooter,
    SettingsDelete,
    InfoBoxWarning,
    AppContent,
    AppEditableHeader,
    AppPage

  }
})
export default class MaintenanceEdit extends Vue {

  @SettingsModule.Getter('getMaintenanceItems')
  private getMaintenanceItems: MaintenanceItem[];

  @SettingsModule.Getter('getMaintenanceItemsStatus')
  private getMaintenanceItemsStatus: LoadState;

  @SettingsModule.Action('fetchMaintenanceItems')
  private fetchMaintenanceItems;

  @SettingsModule.Action('deleteMaintenanceItem')
  private deleteMaintenanceItem;

  @SettingsModule.Action('updateMaintenanceItem')
  private updateMaintenanceItem;

  @SettingsModule.Action('createMaintenanceItem')
  private createMaintenanceItem;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @TriplogModule.Getter('getLatestTrip')
  private getLatestTrip: Trip;

  @TriplogModule.Getter('getLatestTripStatus')
  private getLatestTripStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private input: MaintenanceItemMutation = {
    startDate: null,
    endDate: null,
    startOdometer: null,
    active: true,
    name: '',
    datePeriodValue: null,
    datePeriodUnit: null,
    odometerPeriodKmValue: null,
    periodRelativ: false
  };

  private disabled: boolean = true;
  private dueType: DueType = 'repeat';
  private requestError: string = null;
  private lastKm: number = 0;

  async created() {
    if (this.id) {
      if (this.getMaintenanceItemsStatus !== LoadState.Success) {
        await this.fetchMaintenanceItems();
      }

      this.input = deepCopy(this.item) as MaintenanceItemMutation;

      if (!this.input.odometerPeriodKmValue && !this.input.datePeriodUnit && !this.input.datePeriodValue) {
        this.dueType = 'once';
      }
    }

    if (this.getLatestTripStatus !== LoadState.Success || this.getLatestTrip.vehicleId !== +this.vehicle) {
      await this.fetchLatestTrip({ vehicleId: +this.vehicle });
    }

    if (this.getLatestTrip) {
      if (this.getLatestTrip.endInfo && this.getLatestTrip.endInfo.odometer) {
        this.lastKm = Math.round(this.getLatestTrip.endInfo.odometer / 1000);
      } else if (this.getLatestTrip.startInfo.odometer) {
        this.lastKm = Math.round(this.getLatestTrip.startInfo.odometer / 1000);
      }
    }

    this.disabled = false;
  }

  get minDate() {
    return fnsFormatISO(new Date(), { representation: 'date' });
  }

  get kmLabel() {
    return this.isDueTypeOnce ? 'Km-Stand' : 'Startet bei';
  }

  get dateLabel() {
    return this.isDueTypeOnce ? 'Datum' : 'Startet am';
  }

  get tooltipKm() {
    if (!this.isDueTypeOnce) {
      return 'Die erste Erinnerung wird bei diesem KM-Stand gesendet. Danach wiederholt sich die Erinnerung nach der oben eingegebenen zurückgelegten Strecke.';
    }
  }

  get tooltipDate() {
    if (!this.isDueTypeOnce) {
      return 'Die erste Erinnerung wird an diesem Tag gesendet. Danach wiederholt sich die Erinnerung nach dem oben ausgewählten Intervall.';
    }
  }

  get calculatedStartKm() {
    if (this.input.startOdometer) {
      return this.input.startOdometer / 1000;
    }
  }

  get addMode() {
    return !this.id;
  }

  get item() {
    return this.getMaintenanceItems.find(m => m.id === this.id);
  }

  get vehicle() {
    return this.$route.query.vehicleId;
  }

  get id() {
    if (this.$route.params.id) {
      return parseInt(this.$route.params.id);
    }
    return null;
  }

  get backButton() {
    return { text: 'Zurück zu Erinnerungen', location: this.backLocation };
  }

  get backLocation(): Location {
    return { name: 'maintenanceOverview' };
  }

  get errorLoading() {
    return !this.addMode && this.getMaintenanceItemsStatus !== LoadState.Success && this.getMaintenanceItemsStatus !== LoadState.Loading;
  }

  get errorsExist() {
    return !this.input.name || this.customErrorsExist;
  }

  get customErrorAndNoTitleErrorExists() {
    return !!this.input.name && this.customErrorsExist;
  }

  get customErrorsExist() {
    return (this.dueType === 'once' && !this.input.startDate && !this.input.startOdometer) ||
      (this.dueType === 'repeat' && !this.input.odometerPeriodKmValue && !this.input.datePeriodValue);
  }

  get isDueTypeOnce() {
    return this.dueType === 'once';
  }

  get dueTypeOptions() {
    return [
      { name: 'Einmalig', value: 'once' },
      { name: 'Regelmäßig', value: 'repeat' }
    ];
  }

  get datePeriodOptions() {
    return [
      { name: 'Ohne Datum', value: null, unit: null },
      { name: 'Wöchentlich', value: 1, unit: MaintenanceItemDatePeriodUnit.WEEK },
      { name: 'Monatlich', value: 1, unit: MaintenanceItemDatePeriodUnit.MONTH },
      { name: 'Quartalsweise', value: 3, unit: MaintenanceItemDatePeriodUnit.MONTH },
      { name: 'Halb-Jährlich', value: 6, unit: MaintenanceItemDatePeriodUnit.MONTH },
      { name: 'Jährlich', value: 1, unit: MaintenanceItemDatePeriodUnit.YEAR },
    ]
  }

  private changeDueType(value) {
    this.dueType = value;
    this.input.startDate = null;
    this.input.startOdometer = null;
    this.input.datePeriodUnit = null;
    this.input.datePeriodValue = null;
    this.input.odometerPeriodKmValue = null;
  }

  private changeStartKm(value: number) {
    if (value !== undefined && value !== null) {
      this.input.startOdometer = value * 1000;
    } else {
      this.input.startOdometer = null;
    }
  }

  private changeStartDate(value: string) {
    this.input.startDate = value;
  }

  private changePeriodKmValue(value: number) {
    this.input.odometerPeriodKmValue = value;
    if (value) {
      this.input.startOdometer = (this.lastKm + value) * 1000;
    } else {
      this.input.startOdometer = null;
    }
  }

  private isOptionSelected(option) {
    return option.value === this.input.datePeriodValue && option.unit === this.input.datePeriodUnit;
  }

  private setOption(option) {
    const selected = this.datePeriodOptions[option];
    this.input.datePeriodValue = selected.value;
    this.input.datePeriodUnit = selected.unit;

    if (selected) {
      const startDate = fnsAdd(new Date(), {
        years: selected.unit === MaintenanceItemDatePeriodUnit.YEAR ? selected.value : 0,
        months: selected.unit === MaintenanceItemDatePeriodUnit.MONTH ? selected.value : 0,
        weeks: selected.unit === MaintenanceItemDatePeriodUnit.WEEK ? selected.value : 0
      });
      this.input.startDate = fnsFormatISO(startDate, { representation: 'date' });
    } else {
      this.input.startDate = null;
    }
  }

  private async submit() {
    if (!this.errorsExist) {
      this.disabled = true;

      try {
        if (this.addMode) {
          await this.createMaintenanceItem({ vehicleId: this.vehicle, maintenanceItem: this.input });
        } else {
          await this.updateMaintenanceItem({ id: this.item.id, vehicleId: this.vehicle, maintenanceItem: this.input });
        }
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        console.log(error);
        this.requestError = 'Fehler beim Speichern der Erinnerung. Bitte versuchen sie es später erneut.';
      } finally {
        this.disabled = false;
      }
    }
  }

  private async deleteItem() {
    this.disabled = true;
    try {
      await this.deleteMaintenanceItem({ id: this.item.id, vehicleId: this.vehicle });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } finally {
      this.disabled = false;
    }
  }
}
